import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { AdminArticleDetail, apiAdminGetArticleById, apiFetchArticlePdfBlobUrl, ApiErrorResponse } from '../api/client';
import PortalPageShell from './PortalPageShell';
import PdfViewer from './PdfViewer';

export default function AdminArticleDetailPage() {
  const { id } = useParams();
  const articleId = Number(id);
  const [article, setArticle] = useState<AdminArticleDetail | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    const load = async () => {
      setLoading(true);
      const articleResponse: AdminArticleDetail | ApiErrorResponse = await apiAdminGetArticleById(articleId);

      if (!active) return;

      if ('id' in articleResponse) {
        setArticle(articleResponse);
        setError('');

        if (articleResponse.content_type === 'pdf') {
          try {
            objectUrl = await apiFetchArticlePdfBlobUrl(articleResponse.id);
            if (active) {
              setPdfUrl(objectUrl);
            }
          } catch (pdfError) {
            if (active) {
              setError(pdfError instanceof Error ? pdfError.message : 'Unable to load PDF preview.');
            }
          }
        }
      } else {
        setError(articleResponse.message || 'Unable to load article details.');
      }

      if (active) {
        setLoading(false);
      }
    };

    if (articleId) {
      load();
    }

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [articleId]);

  if (loading) {
    return (
      <PortalPageShell title="Article Details" subtitle="Loading article details.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading details...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell title="Article Details" subtitle="Review article information and preview the PDF.">
      <Stack spacing={6}>
        {error ? <Text color="red.500">{error}</Text> : null}

        {article ? (
          <Stack spacing={5}>
            <Stack p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" spacing={3} bg="gray.50">
              <HStack spacing={3} flexWrap="wrap">
                <Badge colorScheme={article.content_type === 'pdf' ? 'purple' : 'green'}>{article.content_type}</Badge>
                <Badge colorScheme={article.published ? 'green' : 'gray'}>{article.published ? 'published' : 'draft'}</Badge>
              </HStack>
              <Text fontSize="2xl" fontWeight="700">{article.title}</Text>
              <Text color="gray.600">{article.excerpt || 'No excerpt available.'}</Text>
              <Text><strong>Slug:</strong> {article.slug || '-'}</Text>
              <Text><strong>PDF Filename:</strong> {article.pdf_filename || '-'}</Text>
              <Text><strong>Published At:</strong> {article.published_at || '-'}</Text>
              <Text><strong>Created At:</strong> {article.created_at || '-'}</Text>
              <HStack spacing={3} pt={2}>
                <Button as={RouterLink} to={`/admin/articles/${article.id}/edit`} colorScheme="blue" variant="outline">
                  Edit Article
                </Button>
              </HStack>
            </Stack>

            <Box>
              <Text fontWeight="700" fontSize="lg" mb={3}>Article Content</Text>
              {pdfUrl ? (
                <PdfViewer file={pdfUrl} />
              ) : (
                <Text color="gray.600">PDF preview is not available.</Text>
              )}
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </PortalPageShell>
  );
}
